import { OnboardingDraftV1Schema } from "@repo/types";
import { z } from "zod";
import {
  getOnboardingV1StepIndex,
  type OnboardingV1StepId,
} from "./onboarding-v1-step-search";

export const ONBOARDING_V1_DRAFT_STORAGE_KEY =
  "glider_onboarding_v1_draft" as const;
export const ONBOARDING_V1_DRAFT_RETENTION_MS = 7 * 24 * 60 * 60 * 1_000;

// Ordered. `hook` records that nothing is complete yet; it is named for the
// retired opening screen and persisted drafts still carry it.
const ONBOARDING_V1_LOCALLY_COMPLETED_STEP_IDS = [
  "hook",
  "name",
  "profile",
  "interests",
  "projection",
  "pick",
] as const;

const OnboardingV1LocallyCompletedStepSchema = z.enum(
  ONBOARDING_V1_LOCALLY_COMPLETED_STEP_IDS,
);

type OnboardingV1LocallyCompletedStep = z.infer<
  typeof OnboardingV1LocallyCompletedStepSchema
>;

export const OnboardingV1LocalDraftSchema = OnboardingDraftV1Schema.safeExtend({
  lastCompletedStepId: OnboardingV1LocallyCompletedStepSchema.optional(),
});

export type OnboardingV1LocalDraft = z.infer<
  typeof OnboardingV1LocalDraftSchema
>;

export interface OnboardingV1DraftStorage {
  getItem(key: string): string | null;
  removeItem(key: string): void;
  setItem(key: string, value: string): void;
}

function getBrowserStorage(): OnboardingV1DraftStorage | null {
  try {
    return globalThis.localStorage;
  } catch {
    return null;
  }
}

export function createOnboardingV1Draft(
  now = new Date(),
): OnboardingV1LocalDraft {
  return {
    interests: [],
    interestsUnknown: false,
    selectedBlueprintId: null,
    selectedNetwork: "EVM",
    selfDescriptions: [],
    updatedAt: now.toISOString(),
    username: null,
    version: 1,
  };
}

export function clearOnboardingV1Draft(
  storage: OnboardingV1DraftStorage | null = getBrowserStorage(),
): void {
  try {
    storage?.removeItem(ONBOARDING_V1_DRAFT_STORAGE_KEY);
  } catch {
    // Storage can be unavailable in privacy-constrained browser contexts.
  }
}

export function readOnboardingV1Draft(
  storage: OnboardingV1DraftStorage | null = getBrowserStorage(),
  now = new Date(),
): OnboardingV1LocalDraft | null {
  try {
    const serialized = storage?.getItem(ONBOARDING_V1_DRAFT_STORAGE_KEY);
    if (!serialized) {
      return null;
    }

    const parsed = OnboardingV1LocalDraftSchema.safeParse(
      JSON.parse(serialized),
    );
    if (
      !parsed.success ||
      now.getTime() - new Date(parsed.data.updatedAt).getTime() >
        ONBOARDING_V1_DRAFT_RETENTION_MS
    ) {
      clearOnboardingV1Draft(storage);
      return null;
    }

    return parsed.data;
  } catch {
    clearOnboardingV1Draft(storage);
    return null;
  }
}

export function writeOnboardingV1Draft(
  draft: OnboardingV1LocalDraft,
  storage: OnboardingV1DraftStorage | null = getBrowserStorage(),
): boolean {
  const parsed = OnboardingV1LocalDraftSchema.safeParse(draft);
  if (!parsed.success || !storage) {
    return false;
  }

  try {
    storage.setItem(
      ONBOARDING_V1_DRAFT_STORAGE_KEY,
      JSON.stringify(parsed.data),
    );
    return true;
  } catch {
    return false;
  }
}

/** Merges `patch` into the saved draft, or a new one, and saves it. */
export function patchOnboardingV1Draft(
  patch: Partial<OnboardingV1LocalDraft>,
  storage: OnboardingV1DraftStorage | null = getBrowserStorage(),
  now = new Date(),
): void {
  const parsed = OnboardingV1LocalDraftSchema.safeParse({
    ...(readOnboardingV1Draft(storage, now) ?? createOnboardingV1Draft(now)),
    ...patch,
    updatedAt: now.toISOString(),
  });
  if (parsed.success) {
    writeOnboardingV1Draft(parsed.data, storage);
  }
}

export function selectOnboardingV1Blueprint(
  draft: OnboardingV1LocalDraft,
  blueprintId: string,
  now = new Date(),
): OnboardingV1LocalDraft {
  return OnboardingV1LocalDraftSchema.parse({
    ...draft,
    selectedBlueprintId: blueprintId,
    updatedAt: now.toISOString(),
  });
}

export function clearOnboardingV1RecommendationSelection(
  draft: OnboardingV1LocalDraft,
  now = new Date(),
): OnboardingV1LocalDraft {
  return OnboardingV1LocalDraftSchema.parse({
    ...draft,
    selectedBlueprintId: null,
    updatedAt: now.toISOString(),
  });
}

function getEarliestIncompleteStep(
  draft: OnboardingV1LocalDraft,
): Exclude<OnboardingV1StepId, "projection" | "fund"> {
  if (!draft.username) {
    return "name";
  }
  if (draft.selfDescriptions.length === 0) {
    return "profile";
  }
  if (draft.interests.length === 0 && !draft.interestsUnknown) {
    return "interests";
  }
  if (!draft.selectedBlueprintId) {
    return "pick";
  }
  return "account";
}

function getLegacyLastCompletedStep(
  draft: OnboardingV1LocalDraft | null,
): Exclude<OnboardingV1LocallyCompletedStep, "projection"> {
  if (!draft?.username) {
    return "hook";
  }
  if (draft.selfDescriptions.length === 0) {
    return "name";
  }
  if (draft.interests.length === 0 && !draft.interestsUnknown) {
    return "profile";
  }
  if (!draft.selectedBlueprintId) {
    return "interests";
  }
  return "pick";
}

export function getOnboardingV1LastCompletedStep(
  draft: OnboardingV1LocalDraft | null,
): OnboardingV1LocallyCompletedStep {
  return draft?.lastCompletedStepId ?? getLegacyLastCompletedStep(draft);
}

export function advanceOnboardingV1CompletedStep(
  draft: OnboardingV1LocalDraft | null,
  completedStep: OnboardingV1LocallyCompletedStep,
): OnboardingV1LocallyCompletedStep {
  const currentStep = getOnboardingV1LastCompletedStep(draft);
  return ONBOARDING_V1_LOCALLY_COMPLETED_STEP_IDS.indexOf(currentStep) >=
    ONBOARDING_V1_LOCALLY_COMPLETED_STEP_IDS.indexOf(completedStep)
    ? currentStep
    : completedStep;
}

/** The last completed screen, for recovering an unreachable deep link. */
export function getOnboardingV1RecoveryStep(
  draft: OnboardingV1LocalDraft | null,
): Exclude<OnboardingV1StepId, "account" | "fund"> {
  const completedStep = getOnboardingV1LastCompletedStep(draft);
  return completedStep === "hook" ? "name" : completedStep;
}

export function getOnboardingV1ResumeStep(
  draft: OnboardingV1LocalDraft | null,
): OnboardingV1StepId {
  if (!draft) {
    return "name";
  }
  if (draft.lastCompletedStepId === undefined) {
    return getEarliestIncompleteStep(draft);
  }

  switch (draft.lastCompletedStepId) {
    case "hook":
      return "name";
    case "name":
      return "profile";
    case "profile":
      return "interests";
    case "interests":
      return "projection";
    case "projection":
      return "pick";
    case "pick":
      return "account";
  }
}

/**
 * Where saved progress continues once its username is chosen, or null while
 * the username screen still has work to do (nothing saved, or a saved name
 * the account step sent back as taken).
 */
export function getOnboardingV1SavedResumeStep(
  draft: OnboardingV1LocalDraft | null,
): Exclude<OnboardingV1StepId, "name"> | null {
  if (!draft?.username || getOnboardingV1LastCompletedStep(draft) === "hook") {
    return null;
  }
  const step = getOnboardingV1ResumeStep(draft);
  return step === "name" ? null : step;
}

export function resolveOnboardingV1DraftStep(
  requestedStep: OnboardingV1StepId,
  draft: OnboardingV1LocalDraft | null,
): OnboardingV1StepId {
  if (!draft) {
    return "name";
  }

  return getOnboardingV1StepIndex(requestedStep) <=
    getOnboardingV1StepIndex(getOnboardingV1ResumeStep(draft))
    ? requestedStep
    : getOnboardingV1RecoveryStep(draft);
}

export function startOrResumeOnboardingV1Draft(
  storage: OnboardingV1DraftStorage | null = getBrowserStorage(),
  now = new Date(),
): OnboardingV1StepId {
  const storedDraft = readOnboardingV1Draft(storage, now);
  const draft = storedDraft ?? createOnboardingV1Draft(now);
  if (!storedDraft) {
    writeOnboardingV1Draft(draft, storage);
  }

  return getOnboardingV1ResumeStep(draft);
}
